// packages/chordpro/src/lines.js
//
// Классификация одной строки. Здесь живёт единственная коллизия формата: и
// раздел, и аккорд пишутся квадратными скобками.
//
// ПРАВИЛО: строка ровно из одной пары скобок, содержимое которой НЕ
// разбирается как аккорд, — раздел. Всё остальное — аккорды.
//
// Правки, добытые ревью прошлой волны (каждая стоила падения на живом чарте):
//  · квадратные скобки — сильный маркер (в них пишут только имя раздела),
//    круглые — слабый: от них требуется словарное слово, иначе строка подпевки
//    «(...)» резала куплет пополам и её содержимое исчезало;
//  · хвостовое двоеточие на короткой строке достаточно («Half-Chorus:»);
//  · хвостовые украшения снимаются: holychords копирует раздел вместе с
//    галочкой («Intro: ✔»), и она роняла распознавание;
//  · повторители («2x», «x4») — не заголовки.

import { isChordToken } from './chord.js';
import { normalizeSectionName, isLocalSectionWord } from './section-name.js';

const DIRECTIVE_RE = /^\s*\{\s*([a-zA-Z_][a-zA-Z_0-9]*)\s*(?::\s*([\s\S]*?))?\s*\}\s*$/;

const SHORT_FORMS = {
  t: 'title', st: 'subtitle',
  c: 'comment', ci: 'comment_italic', cb: 'comment_box',
  soc: 'start_of_chorus', eoc: 'end_of_chorus',
  sov: 'start_of_verse',  eov: 'end_of_verse',
  sob: 'start_of_bridge', eob: 'end_of_bridge',
  sot: 'start_of_tab',    eot: 'end_of_tab',
  sog: 'start_of_grid',   eog: 'end_of_grid',
  ns: 'new_song', np: 'new_page',
};

const MAX_HEADING_LEN = 32;
const NAME_CHARS = /^[\p{L}\p{N} .\-_]+$/u;
const REPEAT_MARK = /^(?:\d+\s*[xX]?|[xX]\s*\d+)$/u;

/** @returns {{name:string, value:string}|null} */
export function directiveOf(line) {
  const m = DIRECTIVE_RE.exec(String(line ?? ''));
  if (!m) return null;
  let name = m[1].toLowerCase();
  if (SHORT_FORMS[name]) name = SHORT_FORMS[name];
  return { name, value: (m[2] ?? '').trim() };
}

/** Содержимое строки, состоящей ровно из одной пары квадратных скобок. */
export function bracketOnly(line) {
  const m = /^\s*\[([^\]]+)\]\s*$/.exec(String(line ?? ''));
  return m ? m[1].trim() : null;
}

export function isSectionLine(line, notation = 'B') {
  const inner = bracketOnly(line);
  if (inner === null) return false;
  if (isChordToken(inner, notation)) return false;
  // Повторитель («2x», «x4») — не раздел. Проверка нужна ИМЕННО здесь, а не
  // только в isHeadingLine: doc.js открывает блоки по isSectionLine, и без неё
  // строка «[2x]» заводила бы раздел с именем «2x».
  if (REPEAT_MARK.test(inner)) return false;
  return true;
}

// Хвостовое украшение — всё, что не буква, не цифра и не разрешённая
// пунктуация, в самом конце строки. Снимается ТОЛЬКО для распознавания;
// в текст ничего не записывается.
function stripDecoration(line) {
  return String(line ?? '')
    .replace(/[^\p{L}\p{N} .:\-_()[\]]+$/u, '')
    .replace(/\s+$/, '');
}

export function isHeadingLine(line, notation = 'B') {
  if (isSectionLine(line, notation)) return true;
  if (directiveOf(line)) return false;

  const s = stripDecoration(line).trim();
  if (!s || s.length > MAX_HEADING_LEN) return false;

  const square = /^\[(.+)\]$/.exec(s);
  const round  = /^\((.+)\)$/.exec(s);
  if (square || round) {
    const inner = (square ? square[1] : round[1]).trim();
    if (!inner || !NAME_CHARS.test(inner)) return false;
    if (REPEAT_MARK.test(inner)) return false;
    // Квадратные — сильный маркер: достаточно того, что это не аккорд.
    // Круглые — слабый: нужно словарное слово.
    return square ? !isChordToken(inner, notation) : !!normalizeSectionName(inner).family;
  }

  const colon = /^(.+?):$/.exec(s);
  const body = (colon ? colon[1] : s).trim();
  if (!body || !NAME_CHARS.test(body)) return false;
  if (REPEAT_MARK.test(body)) return false;
  if (colon) return true;
  return !!normalizeSectionName(body).family || isLocalSectionWord(body);
}

/** Имя раздела из строки-заголовка. Слова НЕ меняются — только обёртка. */
export function headingTextOf(line) {
  const s = stripDecoration(line).trim();
  const m = /^\[(.*)\]$/.exec(s) ?? /^\((.*)\)$/.exec(s);
  return (m ? m[1] : s).replace(/:+$/, '').trim();
}

/**
 * Строка, целиком состоящая из аккордов, разделённых пробелами.
 * @returns {Array<{tok:string, col:number}>|null}
 */
export function chordOnlyLine(line, notation = 'B') {
  const s = String(line ?? '');
  if (!s.trim()) return null;
  if (s.includes('[')) return null;          // уже inline-форма
  if (directiveOf(s)) return null;

  const out = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (!isChordToken(m[0], notation)) return null;
    out.push({ tok: m[0], col: m.index });
  }
  return out.length ? out : null;
}

// ── Строка аккордов «с деталями» (спека 2026-09-28) ───────────────────────
// chordOnlyLine требует, чтобы КАЖДЫЙ токен был аккордом, и одна деталь
// («(x2)», «Проигрыш:», такт «|», «N.C.», «Hm» в нотации B) делала всю строку
// текстом. Здесь — разбор такой строки на метку, аккорды и хвост-повтор.

const REPEAT_RE = /^\(?\s*(?:[xх×]\s?\d+|\d+\s?[xх×]|\d+\s*(?:раза?|рази|разів|times))\s*\)?$/i;
const otherNotation = (n) => (n === 'H' ? 'B' : 'H');
const isBarToken = (tok) => /^[|/\\-]+$/.test(tok);

/** «x2», «(2 раза)», «3 рази» — пометка повтора. */
export function isRepeatMark(text) {
  return REPEAT_RE.test(String(text ?? '').trim());
}

/** Аккорд в этой нотации, в другой (человек сказал «это аккорды»), или N.C. */
export function isChordish(tok, notation = 'B') {
  return isChordToken(tok, notation) || isChordToken(tok, otherNotation(notation)) || /^N\.?C\.?$/i.test(tok);
}

/**
 * @returns {{label:string|null, chords:Array<{tok:string,col:number}>, comment:string|null}|null}
 *   null — в строке нет ни одного аккорда.
 */
export function splitChordLine(line, notation = 'B') {
  let text = String(line ?? '');
  let comment = null;
  const paren = /\(([^()]*)\)\s*$/.exec(text);
  if (paren) { comment = paren[1].trim(); text = text.slice(0, paren.index) + ' '.repeat(paren[0].length); }
  const toks = [...text.matchAll(/\S+/g)].map((m) => ({ tok: m[0], col: m.index }));
  const first = toks.findIndex((t) => isChordish(t.tok, notation));
  if (first < 0) return null;
  let last = toks.length - 1;
  while (last > first && !isChordish(toks[last].tok, notation)) last--;
  const label = toks.slice(0, first).map((t) => t.tok).join(' ').replace(/:+$/, '').trim() || null;
  const tail = toks.slice(last + 1).map((t) => t.tok).join(' ').trim();
  if (tail) comment = comment ? `${tail} ${comment}` : tail;
  const chords = toks.slice(first, last + 1).filter((t) => !isBarToken(t.tok));
  return { label, chords, comment };
}

/**
 * Строка, которую ПЕРЕВОД вправе считать строкой аккордов: всё между первым и
 * последним аккордом — аккорды или такты, метка — с двоеточием или словарная,
 * хвост — пометка повтора. Строка слов с «A» так не пройдёт.
 * «A E» — два аккорда подряд — строкой аккордов считается: вызывающий код
 * (toChordPro, ручные chordsOp/lineMarks) трактует её как аккорды, а не слова.
 */
export function strictChordLine(line, notation = 'B') {
  const s = String(line ?? '');
  if (!s.trim() || s.includes('[') || directiveOf(s)) return false;
  const p = splitChordLine(s, notation);
  if (!p || !p.chords.length || !p.chords.every((c) => isChordish(c.tok, notation))) return false;
  const toks = [...s.matchAll(/\S+/g)].map((m) => m[0]);
  const labelToks = toks.slice(0, toks.findIndex((t) => isChordish(t, notation)));
  if (labelToks.length && !/:$/.test(labelToks.at(-1)) && !isHeadingLine(labelToks.join(' '), notation)) return false;
  if (p.comment && !isRepeatMark(p.comment)) return false;
  return true;
}
